import { BCMSDate, BCMSRecord, Outlet, useBCMSContext, useModel, useParams, useRoute } from "@bcm-dev/bcms-vite";
import { Card, Merchant, Transaction, Util } from "model";
import React from "react";

import MainContentContainer, { MainContentHeader } from "components/shared/containers/MainContentContainer";
import TabsContainer, { TabItem } from "components/shared/containers/TabsContainer";
import { SVGAsset } from "res";

import "./styles.css";

/**
 * @function SpendInsightsPage
 * @param {DOUBLE} amount - This is the amount for the recent transactions.
 * @param {STRING} date - This is the date.
 * @return {React.Component}
 * @exported true
 * @description The main component for SpendInsightsPage
 * @example
 * <SpendInsightsPage />
 */
export default function SpendInsightsPage(props) {
  const controller = useBCMSContext();
  const navigate = useRoute();
  const model = useModel();
  const cardID = useParams().cardID ?? Util.getDefaultCardID(model);

  BCMSRecord.globalControllerFor([ Merchant, Transaction, Card ], controller);

  const whatActive = [ "/home/spendinsights", "/home/spendinsights/what", `/home/spendinsights/what/${ cardID }` ];
  const whereActive = [ "/home/spendinsights/where", `/home/spendinsights/where/${ cardID }` ];
  const whenActive = [ "/home/spendinsights/when", `/home/spendinsights/when/${ cardID }` ];

  const whereRoute = `/home/spendinsights/where/${ cardID }`;
  const whatRoute = `/home/spendinsights/what/${ cardID }`;
  const whenRoute = `/home/spendinsights/when/${ cardID }`

  const card = Card.initByID(cardID);
  const amount = card.balance.format();
  const date = BCMSDate.init().format("M Y");

  return (
    <MainContentContainer 
      header={
        <MainContentHeader 
          text="Spend Insights"
          onClick={ evt => navigate(`/home/recenttransactions/${ cardID }`) } />
      } 
      className="spend-insights-page">
      
      <div className="top-section">
        <h1 className="amount" data-point="Spend_Insights_Balance">{ amount }</h1>
        
        <p className="sub-text">{date}</p>
        <TabsContainer>
          <TabItem text="WHAT" activeClass="active-tab active" activeRoute={ whatActive } route={ whatRoute } dataPoint="Spend_Insights_What" />
          <TabItem text="WHEN" activeClass="active-tab active" activeRoute={ whenActive } route={ whenRoute } dataPoint="Spend_Insights_When" />
          <TabItem text="WHERE" activeClass="active-tab active" activeRoute={ whereActive } route={ whereRoute } dataPoint="Spend_Insights_Where" />
        </TabsContainer>
      </div>
      <Outlet />
    </MainContentContainer>
  );
}

/**
 * @function GroupDropdownView
 * @param {STRING} groupImage - Sets the group image.
 * @param {DOUBLE} groupName - This is the name of the group.
 * @param {DOUBLE} number - Sets the number of transactions.
 * @param {DOUBLE} totalAmount - Sets the total amount of each transaction in the group.
 * @param {DOUBLE} transactionNumber - Sets the number of transactions where the group image is. 
 * @param {STRING} className - Allows us to set a unique className. 
 * @param {STRING} leftIcon - This is the date.
 * @param {STRING} billerName - This is the date.
 * @param {STRING} date - This is the date.
 * @param {DOUBLE} amount - This is the date.
 * @return {React.Component}
 * @exported true
 * @description This is the dropdown for transaction groups.
 * @example
 * <GroupDropdownView groupImage="IconID" groupName="Grocery" number="7" totalAmount="57.64" transactionNumber="7" className="some-class" leftIcon="PizzaTown" billerName="Pizza Town" date="05/26/2065" amount="10.25" />
 */
export function GroupDropdownView(props) {
  const name = props.name ?? "";
  const logo = props.logo ?? "";
  const onClick = props.onClick ?? undefined;
  const totalAmount = props.totalAmount ?? "$0.00";
  const state = props.state ?? false;
  const className = props.className ?? "";
  const transactionNumber = props.transactionNumber ?? "";
  const number = props.totalNum ?? React.Children.toArray(props.children).length;

  return (
    <div className={ `${ className } group-dropdown-view clickable` }>
      <div className={ `flex-container flex-horizontal align-center-container group-category bottom-border ${ state && "selected" }` } onClick={ onClick }>
          <div className="icon-group">
              <p className="icon-overlay-text left-icon">{ transactionNumber }</p>
              { logo }
          </div>

          <div className="flex-container flex-vertical flex-width">
              <p className="group-name">{ name }</p>
              <p className="sub-text">{ number } Transactions</p>
          </div>

          <div className="flex-container set-width align-center-container">
              <p className="total-amount">{ totalAmount }</p>

              { state && <SVGAsset id="UpChevronIcon" className="up-chevron-icon chevron-icon icon" /> }
              { !state && <SVGAsset id="DownChevronIcon" className="up-chevron-icon chevron-icon icon" /> }
          </div>
      </div>

      {
          state &&
          <div className="transaction-contents dropdown-contents">
              { props.children }
          </div>
      }
    </div>
  );
}